import { Button, Divider, Flex, Progress, Stack, Truncate } from '@eleveo/components-react';
import { ChatIcon, CrossIcon } from '@eleveo/icons-react';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth } from '../../../app/AuthorizationProvider';
import { RequestStatusComponent, RequestSubtypeComponent } from '../../../components/AgentsRequests';
import { DateFormatter } from '../../../components/DateFormatter';
import { Center } from '../../../components/LayoutNext/components/Center';
import { REQUEST_STATE_PROPS } from '../../../config/agentsRequests';
import {
  PtoRequestState,
  RequestPreview,
  RequestState,
  RequestSubtype,
  useGetAgentPtoRequestQuery,
} from '../../../graphql/components';
import { formatSecondsToReadableForm } from '../../../utils/dateRange';
import { formatNameOfPerson } from '../../../utils/helpers';

export type RequestTileProps = {
  isExpanded: boolean;
  onCancel?: () => void;
  request: RequestPreview;
  updateStateOfRequest: (newRequest: RequestPreview, newState: RequestState) => Promise<void>;
};

export const RequestPTOTile = ({ request, onCancel, isExpanded, updateStateOfRequest }: RequestTileProps) => {
  const { t } = useTranslation();
  const { userId: agentId, timezone } = useAuth();

  const scrollToViewRef = useCallback((elRef: HTMLDivElement) => {
    /**
     * TODO: investigate if this could be handled more properly...
     * see https://fosstodon.org/@tahazsh/112128065110906980
     */
    // wait for opening animation to be executed
    setTimeout(() => {
      // ref is only needed to scroll into view, this block will be executed only when accordion item is expanded and data got loaded successfully
      elRef?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
    }, 200); // TODO: 200ms is the current transition time set on UI kit for opening/closing animation, which will need to be changed in near future
  }, []);

  const {
    data: ptoRequestData,
    loading: ptoRequestLoading,
    error: ptoRequestError,
  } = useGetAgentPtoRequestQuery({
    variables: {
      agentId,
      ptoRequestId: request.id,
    },
    fetchPolicy: 'network-only',
    skip: !isExpanded,
    notifyOnNetworkStatusChange: true, // Needed to run `onCompleted` after `refetch()`
    onCompleted: (data) => {
      if ((data.agentPTORequest.state as unknown as RequestState) !== request.state) {
        updateStateOfRequest(request, data.agentPTORequest.state as unknown as RequestState);
      }
    },
  });

  const ptoRequest = ptoRequestData?.agentPTORequest;

  return (
    <>
      {ptoRequestLoading && (
        <Center>
          <Progress className="mx-auto py-40" />
        </Center>
      )}

      {!ptoRequestLoading && !ptoRequestError && ptoRequest && (
        <>
          <div className="px-20 pt-10 pb-15" ref={scrollToViewRef}>
            <Flex className="w-full" column>
              <Flex alignItems="baseline" justifyContent="between" className="w-full mb-10" gap={[10, 0]} wrap>
                <Flex alignItems="center" className="max-w-full" wrap gap={[10, 0]}>
                  <span className="text-sm">{t('form.status')}:</span>
                  <RequestStatusComponent
                    status={ptoRequest?.state as unknown as RequestState}
                    testId="agent-portal:monthly-view:requests-sidebar:pto-request-status"
                  />
                </Flex>
                <Flex
                  justifyContent="end"
                  className="text-right text-sm flex-1 wrapper__tiny-column-gap"
                  testId="agent-portal:monthly-view:requests-sidebar:pto-request-createdAt-timestamp"
                  wrap
                >
                  {t('form.created')}:
                  <DateFormatter
                    className="font-bold"
                    options={{ dateStyle: 'short', timeStyle: 'short', timeZone: timezone }}
                  >
                    {new Date(ptoRequest?.createdAt)}
                  </DateFormatter>
                </Flex>
              </Flex>
              <RequestSubtypeComponent
                testId="agent-portal:monthly-view:requests-sidebar:pto-request-subtype"
                type={ptoRequest?.type as unknown as RequestSubtype}
              />
              <div className="mr-auto mt-10 text-sm">
                <Flex
                  className="gap-5"
                  testId="agent-portal:monthly-view:requests-sidebar:pto-request-start-date-time-timestamp"
                >
                  <span className="mr-auto">{t('time.start')}:</span>
                  <DateFormatter
                    className="font-bold"
                    options={{ dateStyle: 'short', timeStyle: 'short', timeZone: timezone }}
                  >
                    {new Date(ptoRequest?.dateRange.startDateTime)}
                  </DateFormatter>
                </Flex>
                <Flex
                  className="gap-5"
                  testId="agent-portal:monthly-view:requests-sidebar:pto-request-end-date-time-timestamp"
                >
                  <span className="mr-auto">{t('time.end')}:</span>
                  <DateFormatter
                    className="font-bold"
                    options={{ dateStyle: 'short', timeStyle: 'short', timeZone: timezone }}
                  >
                    {new Date(ptoRequest?.dateRange.endDateTime)}
                  </DateFormatter>
                </Flex>
                <Flex className="gap-5" testId="agent-portal:monthly-view:requests-sidebar:pto-request-total-duration">
                  <span className="mr-auto">{t('time.total')}:</span>
                  <p className="font-bold">
                    {formatSecondsToReadableForm(
                      Math.floor(
                        (new Date(ptoRequest?.dateRange.endDateTime).getTime() -
                          new Date(ptoRequest?.dateRange.startDateTime).getTime()) /
                          1000
                      ),
                      t,
                      true
                    )}
                  </p>
                </Flex>
              </div>
            </Flex>
            {ptoRequest?.audits.map(({ message: auditMessage, toState, who, when }) => {
              if (toState === RequestState.PendingApproval && !auditMessage) {
                return null;
              }

              return (
                <React.Fragment key={`${toState}-${when}`}>
                  <Divider className="mt-10" />
                  <Stack
                    className="w-full"
                    direction="vertical"
                    spacing={0}
                    testId="agent-portal:monthly-view:requests-sidebar:pto-request-audit-log"
                  >
                    {toState !== RequestState.PendingApproval && (
                      <Stack className="mt-10 text-sm">
                        <Truncate>
                          <span
                            className="font-bold"
                            data-test-id="agent-portal:monthly-view:requests-sidebar:pto-request-audit-log:to-state"
                          >
                            {t(REQUEST_STATE_PROPS[toState].nameKey)}:{' '}
                          </span>
                          <span
                            className="font-bold"
                            data-test-id="agent-portal:monthly-view:requests-sidebar:pto-request-audit-log:person"
                          >
                            {formatNameOfPerson(who.firstName, who.lastName)}
                          </span>
                        </Truncate>
                        <DateFormatter
                          className="flex-none font-bold ml-auto"
                          options={{ dateStyle: 'short', timeStyle: 'short', timeZone: timezone }}
                        >
                          {new Date(when)}
                        </DateFormatter>
                      </Stack>
                    )}
                    {auditMessage && (
                      <Flex
                        className="mt-10"
                        testId="agent-portal:monthly-view:requests-sidebar:pto-request-audit-log:message"
                      >
                        <ChatIcon className="text-blue-700 text-lg" />
                        <span className="ml-1 break-words overflow-auto">{auditMessage}</span>
                      </Flex>
                    )}
                  </Stack>
                </React.Fragment>
              );
            })}
            {/* Only Pending-approval requests can be canceled by agents! */}
            {ptoRequest?.state === PtoRequestState.PendingApproval && onCancel && (
              <Flex justifyContent="end">
                <Button
                  color="warning"
                  isSmall
                  onClick={onCancel}
                  variant="outline"
                  testId="agent-portal:monthly-view:requests-sidebar:pto-request-audit-log:cancel-button"
                >
                  <CrossIcon /> {t('schedule.request.cancelRequest')}
                </Button>
              </Flex>
            )}
          </div>
        </>
      )}
    </>
  );
};
